import React, {
	useState,
	useEffect,
	useCallback,
	useRef,
	useMemo,
} from "react";
import { getProductInfoUrl } from "../../conf";
import { AnimatePresence, motion } from "framer-motion";
import useResource from "../../hooks/useResource";
import getImgUrl from "../../utils/getImgUrl";
import { Loader } from "../CoreUI";
import {
	ProductViewActionBar,
	ProductViewActionOverlay,
	ProductViewNavBar,
} from "../ProductViewComponents/ProductViewCore";
import ProductViewDetails from "../ProductViewComponents/ProductViewDetails";
import "./product-view.css";
import anim from "../../utils/anim";
import GET from "../../utils/GET";
import useTrack, { eventTypes } from "../../hooks/useTrack";
import parseUtm from "../../utils/parseUtm";

let purchases = GET("purchases");
try {
	purchases = JSON.parse(purchases);
} catch (err) {
	purchases = null;
}
const ProductView = React.memo(
	({ pdpInfo = false, innerHeight, offsetTop, setPdpInfo }) => {
		if (!pdpInfo || !pdpInfo.remote_id) {
			return;
		}

		const [productViewData, setProductViewData] = useState(null);
		const [activeImage, setActiveImage] = useState(null);
		const [{ data, error, loading }, , , , resetData] = useResource(
			getProductInfoUrl,
			{
				method: "POST",
				body: JSON.stringify({
					// product_id: "4984d4e0-f598-4de9-a2b7-a9c7e16eebe2", // has variants
					// product_id: "22c955dd-0f7b-4420-bbb8-5691c0228bc5",
					// product_id: "8aab1a8f-ea39-4488-b16e-d0e3fc3c67be", // has similar
					product_id: pdpInfo.remote_id,
					purchases: purchases || [],
				}),
				headers: {
					"Content-Type": "application/json",
				},
			}
		);

		const track = useTrack();
		const [firedOpen, setFiredOpen] = useState(false);

		const [selectedVariant, setSelectedVariant] = useState({});
		const [quantity, setQuantity] = useState(1);
		const [purchaseError, setPurchaseError] = useState(false);

		const [showActionOverlay, setShowActionOverlay] = useState(false);
		const [extraPadding, setExtraPadding] = useState(true);

		const [showActionBarLoader, setShowActionBarLoader] = useState(false);

		const navRef = useRef(null);
		const rootRef = useRef(null);

		const productViewEventProps = useMemo(() => {
			if (
				!productViewData ||
				!productViewData.product ||
				!productViewData.brand
			) {
				return {};
			}
			const { product, brand } = productViewData;
			const {
				remote_id,
				price,
				discounted_price,
				brand_id,
				name,
				id,
				variants,
			} = product;
			const retValue = {
				product: name,
				product_id: id || "",
				product_remote_id: remote_id,
				brand: brand.name,
				price: price || "",
				discounted_price: discounted_price || "",
				brand_id,
				variants: JSON.stringify(variants),
			};
			return retValue;
		}, [productViewData]);

		const isSingleVariant = useMemo(() => {
			if (!productViewData?.product?.variants) {
				return null;
			}
			return productViewData.product.variants.single_variant || false;
		}, [productViewData]);

		const handlePDPClose = useCallback(() => {
			resetData();
			track(eventTypes.WIDGET_PDP_CLOSE, {
				...productViewEventProps,
			});
			setPdpInfo(false);
		}, [setPdpInfo, resetData, track, productViewEventProps]);

		const navToTop = useCallback(() => {
			if (!navRef) {
				return;
			}
			navRef.current.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}, []);

		const handleProductViewData = useCallback((callback) => {
			setProductViewData((productViewData) => callback(productViewData));
		}, []);

		const handleActionOverlayOpen = useCallback(() => {
			setShowActionOverlay(true);
		}, []);
		const handleActionOverlayClose = useCallback(() => {
			track(eventTypes.WIDGET_PDP_VARIANT_SCREEN_CLOSE, {
				...productViewEventProps,
			});
			setShowActionOverlay(false);
		}, [productViewEventProps, track]);

		const handlePurchase = useCallback(() => {
			if (GET("full") || GET("platform")) {
				return;
			}
			const baseUrl = productViewData.brand.shopify_shop;
			if (!baseUrl || !selectedVariant.variant_id) {
				setPurchaseError(
					"Whoops! We are unable to purchase this product at the moment. Please try again later"
				);
				return setShowActionOverlay(false);
			}
			track(eventTypes.WIDGET_PDP_CHECKOUT_CLICK, {
				...productViewEventProps,
				display_variant_screen: showActionOverlay,
				selected_variant: selectedVariant.variant_id,
				quantity,
			});
			const url = `${baseUrl}/cart/${
				selectedVariant.variant_id
			}:${quantity}`;
			setShowActionBarLoader(true);
			// const productWindow = window.open();
			// productWindow.opener = null;
			if (window.parent) {
				return (window.parent.location.href = url);
			}
			return (window.location.href = url);
		}, [
			productViewData,
			quantity,
			selectedVariant,
			productViewEventProps,
			showActionOverlay,
			track,
		]);

		const handleScroll = useCallback(() => {
			if (!rootRef?.current) {
				return;
			}
			const scroll = rootRef.current.scrollTop;
			if (scroll > 20) {
				setExtraPadding(false);
			} else {
				setExtraPadding(true);
			}
		}, []);

		useEffect(() => {
			if (!error) {
				return;
			}
			let { masked_url: url, utmFlags, overrideUtm } = pdpInfo;
			url = parseUtm(url, utmFlags, overrideUtm);

			const productWindow = window.open();
			productWindow.opener = null;
			productWindow.location = url;

			handlePDPClose();
		}, [error, setPdpInfo, pdpInfo, handlePDPClose]);

		useEffect(() => {
			if (!data) {
				return;
			}
			setProductViewData(data);
			const {
				product: { photo_url, image_key },
			} = data;
			setActiveImage({
				photo_url,
				image_key,
			});
		}, [data]);

		useEffect(() => {
			if (!isSingleVariant) {
				return;
			}
			setSelectedVariant({
				variant_id: productViewData.product.variants.variant_id,
			});
		}, [isSingleVariant, productViewData]);

		useEffect(() => {
			if (firedOpen || !pdpInfo) {
				return;
			}
			const {
				remote_id,
				brand,
				price_str,
				discounted_price_str,
				name,
				id,
			} = pdpInfo;

			track(eventTypes.WIDGET_PDP_OPEN, {
				product: name,
				product_id: id,
				product_remote_id: remote_id,
				brand: brand,
				price: price_str || "",
				discounted_price: discounted_price_str || "",
			});
			setFiredOpen(true);
		}, [firedOpen, track, pdpInfo]);

		useEffect(() => {
			if (!productViewData || !productViewData.product) {
				return;
			}
			track(eventTypes.WIDGET_PDP_DISPLAY, {
				...productViewEventProps,
			});
		}, [track, productViewData, productViewEventProps]);

		const product = productViewData?.product || false;

		return (
			<motion.section
				className="product-view-root-container"
				variants={anim.pdp}
				key="product-view-root-container"
				initial="initial"
				animate="animate"
				exit="exit"
				style={{ top: `${offsetTop}px`, height: `${innerHeight}px` }}
				ref={rootRef}
				onScroll={handleScroll}
			>
				{loading && <Loader className="product-view-loader" center />}
				{productViewData && product && (
					<>
						<ProductViewNavBar
							handleClose={handlePDPClose}
							product={product}
							productViewEventProps={productViewEventProps}
						/>
						<motion.img
							src={getImgUrl(activeImage)}
							alt={product.name}
							className="product-view-primary-img"
							variants={anim.dialogItem}
							style={{ maxHeight: innerHeight / 1.9 }}
						/>
						<section
							className="product-view-details-container"
							ref={navRef}
							style={{ top: innerHeight / 2.2 }}
						>
							<ProductViewDetails
								data={productViewData}
								setProductViewData={handleProductViewData}
								setActiveImage={setActiveImage}
								navToTop={navToTop}
								error={purchaseError}
								setError={setPurchaseError}
								extraPadding={extraPadding}
								offsetTop={offsetTop}
								productViewEventProps={productViewEventProps}
							/>
							<ProductViewActionBar
								actionOverlay={showActionOverlay}
								handleActionOverlay={handleActionOverlayOpen}
								isSingleVariant={isSingleVariant}
								handlePurchase={handlePurchase}
								quantity={quantity}
								setQuantity={setQuantity}
								maxQuantity={product.max_quantity || 5}
								loading={showActionBarLoader}
								productViewEventProps={productViewEventProps}
							/>
						</section>
						<AnimatePresence>
							{showActionOverlay && (
								<ProductViewActionOverlay
									product={product}
									style={{
										top: `${offsetTop}px`,
										height: `${innerHeight}px`,
									}}
									selectedVariant={selectedVariant}
									setSelectedVariant={setSelectedVariant}
									quantity={quantity}
									error={purchaseError}
									setError={setPurchaseError}
									handleClose={handleActionOverlayClose}
								/>
							)}
						</AnimatePresence>
					</>
				)}
			</motion.section>
		);
	}
);

export default ProductView;
